import React from "react";
import { api } from "../services/api.js";
import { translations } from "../i18n/translations.js";
import { interpolate, languageLocale, localizeCatalogText, normalizeLanguage, translateMessage } from "../i18n/localization.js";
const LanguageContext = React.createContext(null);
function savedLanguage() {
  try {
    return normalizeLanguage(localStorage.getItem("storefront_lang") || localStorage.getItem("manasek_lang"));
  } catch {
    return "ar";
  }
}
function LanguageProvider({ children }) {
  const [language, updateLanguage] = React.useState(savedLanguage);
  const [catalog, setCatalog] = React.useState({ ar: {}, en: {} });
  const setLanguage = React.useCallback(value => updateLanguage(normalizeLanguage(value)), []);
  React.useLayoutEffect(() => {
    document.documentElement.dir = language === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = language;
    try { localStorage.setItem("storefront_lang", language); } catch { /* Storage can be disabled in private browsing. */ }
  }, [language]);
  React.useEffect(() => {
    let active = true;
    api.get("/products/translations/catalog").then(response => {
      if (active) setCatalog({ ar: response.data?.translations?.ar || {}, en: response.data?.translations?.en || {} });
    }).catch(() => {});
    return () => { active = false; };
  }, []);
  const t = React.useCallback((key, values) => interpolate(translations[language]?.[key] || translations.en[key] || key, values), [language]);
  const tText = React.useCallback((text, values) => translateMessage(text, language, values), [language]);
  const tProd = React.useCallback((text, record, field) => localizeCatalogText(text, language, catalog, record, field), [language, catalog]);
  const value = React.useMemo(() => ({ language, setLanguage, t, tText, tProd, locale: languageLocale(language), isRTL: language === "ar" }), [language, setLanguage, t, tText, tProd]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}
function useLanguage() {
  const l = React.useContext(LanguageContext);
  if (!l) throw new Error("useLanguage must be used within a LanguageProvider");
  return l;
}
export { LanguageContext, LanguageProvider, useLanguage };
